import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { useAuth } from '../AuthContext'; 
import SpyglassBrandIcon from '../components/SpyglassBrandIcon'; 

const LoginPage: React.FC = () => {
  const [localUsername, setLocalUsername] = useState('spyglass'); 
  const [localPassword, setLocalPassword] = useState(''); 
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const auth = useAuth(); 
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await auth.login(localUsername, localPassword);
      navigate('/'); 
    } catch (err) {
      console.error('Login attempt failed in component:', err);
    }
  };

  React.useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      navigate('/'); 
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-8 py-12 bg-white font-sans">
      <div className="w-full max-w-lg space-y-12">
        
        {/* Hero Section */}
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <SpyglassBrandIcon />
          </div>
          <div>
            <h1 className="text-4xl font-light text-gray-900 tracking-tight leading-tight">
              Welcome Back
            </h1>
            <p className="text-lg font-light text-gray-600 mt-3">
              Sign in to continue to SpyGlass AI
            </p>
          </div>
        </div>

        {/* Login Form Card */}
        <div className="bg-white/80 backdrop-blur-sm border-0 shadow-xl shadow-gray-100/50 hover:shadow-2xl hover:shadow-gray-100/60 transition-all duration-500 ease-out rounded-2xl">
          <div className="p-8 space-y-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Username Field */}
              <div className="space-y-2">
                <label 
                  htmlFor="username" 
                  className="block text-sm font-medium text-gray-900"
                >
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={localUsername} 
                  onChange={(e) => setLocalUsername(e.target.value)}
                  onFocus={() => setFocusedField('username')}
                  onBlur={() => setFocusedField(null)}
                  disabled={auth.isLoading}
                  className={`
                    w-full h-12 px-4 text-sm font-light
                    bg-white/80 border-2 rounded-2xl backdrop-blur-sm
                    transition-all duration-300 ease-out
                    placeholder:text-gray-400 placeholder:font-light
                    focus:outline-none
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${focusedField === 'username'
                      ? 'border-blue-500 shadow-lg shadow-blue-500/20 bg-white' 
                      : localUsername
                        ? 'border-gray-300 shadow-md shadow-gray-100/50 bg-white'
                        : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md hover:shadow-gray-100/50 bg-white/60'
                    }
                  `}
                />
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label 
                  htmlFor="password" 
                  className="block text-sm font-medium text-gray-900"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={localPassword} 
                  onChange={(e) => setLocalPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  disabled={auth.isLoading}
                  className={`
                    w-full h-12 px-4 text-sm font-light
                    bg-white/80 border-2 rounded-2xl backdrop-blur-sm
                    transition-all duration-300 ease-out
                    placeholder:text-gray-400 placeholder:font-light
                    focus:outline-none
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${focusedField === 'password'
                      ? 'border-blue-500 shadow-lg shadow-blue-500/20 bg-white' 
                      : localPassword
                        ? 'border-gray-300 shadow-md shadow-gray-100/50 bg-white'
                        : 'border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md hover:shadow-gray-100/50 bg-white/60'
                    }
                  `}
                />
              </div>

              {/* Error Message */}
              {auth.error && ( 
                <div className="text-center p-3 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm font-medium text-red-700">{auth.error}</p>
                </div>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={auth.isLoading || !localUsername || !localPassword}
                className={`
                  w-full h-12 text-base font-medium rounded-2xl
                  bg-blue-600 hover:bg-blue-700 active:bg-blue-800
                  text-white border-0 shadow-lg shadow-blue-600/25
                  transition-all duration-300 ease-out
                  transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-xl hover:shadow-blue-600/30
                  disabled:transform-none disabled:opacity-50 disabled:shadow-none
                  disabled:bg-gray-300 disabled:hover:bg-gray-300
                `}
              >
                {auth.isLoading ? 'Signing in...' : 'Sign In'}
              </button>

              {/* Help Text */}
              <div className="text-center">
                <p className="text-xs font-light text-gray-500">
                  Secure access to your SpyGlass AI dashboard
                </p>
              </div>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;
